import type { Layout } from 'react-grid-layout';
import { GRID_CONFIG, GRID_SIZE, ROW_HEIGHT } from '../constants';
import type { DashboardLayouts } from '../services/dashboardLayoutTypes';
import type { DashboardWidget } from '../DashboardGrid';
import { generateAutoLayout, mergeLayouts } from './layoutHelpers';

export type DashboardBreakpoint = keyof typeof GRID_CONFIG.COLS;
export interface WidgetPlacement { x: number; y: number; breakpoint: DashboardBreakpoint }

/** RGL uses (containerWidth + margin) / columns for its horizontal stride. */
export const placementFromClient = (
  clientX: number, clientY: number, rect: Pick<DOMRect, 'left' | 'top' | 'width'>,
  width: number, breakpoint: DashboardBreakpoint,
): WidgetPlacement | undefined => {
  if (![clientX, clientY, rect.left, rect.top, rect.width, width].every(Number.isFinite) || rect.width <= 0 || width <= 0) return;
  const scale = rect.width / width;
  return {
    x: Math.max(0, Math.round((clientX - rect.left) / scale / ((width + GRID_SIZE) / GRID_CONFIG.COLS[breakpoint]))),
    y: Math.max(0, Math.round((clientY - rect.top) / scale / (ROW_HEIGHT + GRID_SIZE))),
    breakpoint,
  };
};

const overlaps = (a: Layout, b: Layout) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/** Keep a hidden widget's old size; choose a bounded free slot at every breakpoint. */
export const placeWidgetInLayouts = (
  layouts: DashboardLayouts, widget: DashboardWidget, activeIds: ReadonlySet<string>, placement?: WidgetPlacement,
): DashboardLayouts | null => {
  if (placement && (![placement.x, placement.y].every(Number.isFinite) || !(placement.breakpoint in GRID_CONFIG.COLS))) return null;
  const result = { ...layouts };
  for (const breakpoint of Object.keys(GRID_CONFIG.COLS) as DashboardBreakpoint[]) {
    const cols = GRID_CONFIG.COLS[breakpoint];
    const items = layouts[breakpoint] || [];
    const previous = items.find(item => item.i === widget.id);
    const item = previous
      ? mergeLayouts([previous], [widget], cols)[0]
      : generateAutoLayout([widget], cols)[0];
    const occupants = items.filter(other => other.i !== widget.id && activeIds.has(other.i));
    const requested = placement?.breakpoint === breakpoint ? placement : previous || { x: 0, y: 0 };
    item.x = Math.max(0, Math.min(Math.round(requested.x), cols - item.w));
    item.y = Math.max(0, Math.min(Math.round(requested.y), GRID_CONFIG.MAX_ROWS - item.h));
    const fits = () => item.y + item.h <= GRID_CONFIG.MAX_ROWS && !occupants.some(other => overlaps(item, other));
    if (!fits()) {
      // Bounded nearest-slot search; reject full canvases rather than hide a widget below maxRows.
      let best: { x: number; y: number; distance: number } | undefined;
      for (let y = 0; y <= GRID_CONFIG.MAX_ROWS - item.h; y++) {
        for (let x = 0; x <= cols - item.w; x++) {
          const distance = Math.abs(x - item.x) + Math.abs(y - item.y);
          if ((!best || distance < best.distance) && !occupants.some(other => overlaps({ ...item, x, y }, other))) best = { x, y, distance };
        }
      }
      if (!best) return null;
      item.x = best.x; item.y = best.y;
    }
    result[breakpoint] = [...items.filter(other => other.i !== widget.id), item];
  }
  return result;
};
